import infoStyles from "../components/builder/BuilderInfo.module.css";
import { useEffect, useMemo, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import { ButtonStatus, CompanyType, FileType } from "../common/constants";
import PageBuilder from "../components/builder/PageBuilder";
import Select from "../components/Select";
import TextInput from "../components/TextInput";
import Uploader from "../components/Uploader";
import AlertDialog from "../components/dialogs/AlertDialog";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import { DialogStatus } from "../enums";
import typo from "../typography.module.css";
import styles from "./EventEditor.module.css";
import { PageBuilderProvider } from "../components/builder/PageBuilderContext";

const normalizeEventStands = (stands) => {
    if (!Array.isArray(stands)) return [];

    return stands
        .map((stand) => ({
            name: (stand?.name || '').trim(),
            company_name: (stand?.company_name || '').trim(),
            description: (stand?.description || '').trim(),
            company_logo: (stand?.company_logo || '').trim() || null,
            company_id: stand?.company_id || null,
        }))
        .filter((stand) => stand.name && stand.company_name && stand.description);
}

const EventEditor = () => {

    const navigate = useNavigate()
    const { eventId } = useParams();
    const [event, setEvent] = useState(null)
    const [status, setStatus] = useState(
        [
            { id: 0, name: 'Bozza', value: 'draft' },
            { id: 1, name: 'Pubblico', value: 'public' },
            { id: 2, name: 'Nascosto', value: 'hidden' }
        ]
    )

    const [loading, setLoading] = useState(false)
    const [savingAction, setSavingAction] = useState(null)
    const [successAction, setSuccessAction] = useState(null)
  const [lastSavedAt, setLastSavedAt] = useState(null)
    const [alert, setAlert] = useState({ open: false, title: '', text: '', actions: [], status: DialogStatus.Default })
    const [uploaderReset, setUploaderReset] = useState(false)
    const [sections, setSections] = useState([])
    const [draftSections, setDraftSections] = useState([])
    const [builderVersion, setBuilderVersion] = useState('published')
    const [builderKey, setBuilderKey] = useState(0)
    const [eventPartnerCompanies, setEventPartnerCompanies] = useState([])

    useEffect(() => {

        const getEvent = async () => {
            try {
                const e = await api.get(`/admin/events/${eventId}`)
                setEvent({
                    ...e,
                    stands: Array.isArray(e?.stands) ? e.stands : []
                })
                setSections(e.content || [])
                setDraftSections(e.draft || e.content || [])
                setBuilderVersion('published')
                setBuilderKey(prev => prev + 1)
                initializeStatus(e.status)
            }
            catch (e) {
                console.error(e)
            }
        }


        if (!event && eventId !== 'create') {
            getEvent()

        } else if (eventId === "create") {
            setEvent({
                name: "",
                description: "",
                status: "draft",
                thumbnail: "",
                stands: []
            })
        }
    }, [eventId])

    useEffect(() => {
        const loadEventPartnerCompanies = async () => {
            try {
                const response = await api.get(`/admin/companies?page=0&per=500&company_type=${CompanyType.EventPartner}`)
                setEventPartnerCompanies((response?.companies || []).map((company) => ({
                    id: company.id,
                    value: company.id,
                    name: company.name,
                    logo: company.logo || null,
                })))
            } catch (error) {
                console.error(error)
            }
        }
        loadEventPartnerCompanies()
    }, [])

    useEffect(() => {
        if (!successAction) {
            return
        }

        const timeout = setTimeout(() => {
            setSuccessAction(null)
        }, 2000)

        return () => clearTimeout(timeout)
    }, [successAction])

    const initializeStatus = (status) => {
        switch (status) {
            case 'draft':
                setStatus([
                    { id: 0, name: 'Bozza', value: 'draft' },
                    { id: 1, name: 'Pubblico', value: 'public' },
                ])
                break
            case 'public':
                setStatus([
                    { id: 1, name: 'Pubblico', value: 'public' },
                    { id: 2, name: 'Nascosto', value: 'hidden' }
                ])
                break
            case 'hidden':
                setStatus([
                    { id: 1, name: 'Pubblico', value: 'public' },
                    { id: 2, name: 'Nascosto', value: 'hidden' }
                ])
                break
            default: return
        }
    }

    const update = async (contentPage, draftPage, action = 'publish') => {
        setLoading(true)
        try {
            let body = {
                name: event.name,
                description: event.description,
                location: event.location,
                slug: event.slug,
                status: event.status,
                thumbnail: event.thumbnail,
                content: contentPage,
                draft: draftPage,
                starts_at: event.starts_at,
                ends_at: event.ends_at,
                stands: normalizeEventStands(event.stands),
            }

            const updated = await api.put(`/admin/events/${event.id}`, body)

            setEvent(updated)
            setSections(updated.content || [])
            setDraftSections(updated.draft || updated.content || [])
            initializeStatus(updated.status)
            setSuccessAction(action)
      setLastSavedAt(new Date().toISOString())

            setUploaderReset(!uploaderReset)
        }
        catch (e) {
            console.error(e)
            setAlert({
                open: true,
                title: 'Operazione Fallita',
                text: e.detail,
                status: DialogStatus.Error,
                actions: [
                    {
                        label: 'OK',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                        }
                    }
                ]
            })
        }
        setLoading(false)

    }

    const create = async (contentPage, draftPage, action = 'publish') => {
        setLoading(true)
        try {
            let body = {
                name: event.name,
                description: event.description,
                location: event.location,
                thumbnail: event.thumbnail,
                slug: event.slug,
                status: event.status,
                starts_at: event.starts_at,
                ends_at: event.ends_at,
                content: contentPage,
                draft: draftPage,
                stands: normalizeEventStands(event.stands),
            }

            const created = await api.post(`/admin/events`, body)

            setEvent(created)
            setSections(created.content || [])
            setDraftSections(created.draft || created.content || [])
            initializeStatus(created.status)
            if (eventId === 'create') {
                navigate(`/events/${created.id}`)
            }
            setSuccessAction(action)
      setLastSavedAt(new Date().toISOString())

            setUploaderReset(!uploaderReset)

        }
        catch (e) {
            console.error(e)
            setAlert({
                open: true,
                title: 'Operazione Fallita',
                text: e.detail,
                status: DialogStatus.Error,
                actions: [
                    {
                        label: 'OK',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                        }
                    }
                ]
            })
        }
        setLoading(false)
    }

    const save = async (page, activeVersion = 'published', action = 'publish') => {
        let nextPublishedPage = sections
        let nextDraftPage = draftSections

        if (activeVersion === 'draft') {
            if (action === 'save_draft') {
                nextDraftPage = page
            } else {
                nextPublishedPage = page
                nextDraftPage = page
            }
        } else {
            nextPublishedPage = page
            if (action === 'export_to_draft') {
                nextDraftPage = page
            }
        }

        setSavingAction(action)
        try {
            if (event.id) {
                await update(nextPublishedPage, nextDraftPage, action)
            }
            else {
                await create(nextPublishedPage, nextDraftPage, action)
            }
        }
        finally {
            setSavingAction(null)
        }
    }

    const saveDisabled = useMemo(() => {
        if (!event) {
            return true
        }

        if (event.status === 'draft') {
            return event.name.trim() === '' || event.slug.trim() === '' || loading || savingAction !== null
        }

        if (event.status === 'public') {
            return event.name.trim() === '' ||
                event.description?.trim() === '' ||
                event.slug.trim() === '' ||
                event.thumbnail === undefined || event.thumbnail === null ||
                event.thumbnail?.trim() === '' ||
                event.starts_at === '' || event.starts_at === null ||
                event.ends_at === '' || event.ends_at === null ||
                loading ||
                savingAction !== null
        }
    }, [event, loading, savingAction])

    const editBar =
        <div className={infoStyles.edit}>
            {event &&
                <div className={infoStyles.card}>
                    <a rel="noreferrer" target="_blank" href={`/events/${event.id}/preview`} className={infoStyles.previewLink}>APRI PREVIEW</a>
                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>STATO</div>
                        <Select appearance="modern" selected={status.find(s => s.value === event?.status)} options={status} onSelect={(value) => {
                            setEvent((prev) => {
                                prev.status = value.value
                                return { ...prev }
                            })
                        }} />
                    </div>

                    <div className={infoStyles.editSection}>

                        <div className={infoStyles.label}>NOME</div>
                        <TextInput appearance="modern"
                            style={{ fontSize: '.75rem' }}
                            type="text"
                            value={event?.name}
                            placeholder="Nome"
                            error={event?.name.trim() === ''}
                            errorMessage="Campo obbligatorio"
                            onKeyUp={(value) => {
                                setEvent((prev) => {
                                    prev.name = value
                                    prev.slug = value?.replaceAll('-', '').replace(/[^a-zA-Z0-9\s-]/g, '') // Rimuovi tutti i caratteri tranne lettere e numeri
                                        .replace(/\s+/g, '-') // Sostituisci spazi con trattini
                                        .toLowerCase()
                                        .trim();
                                    return { ...prev }
                                })
                            }} />
                    </div>

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>SLUG</div>
                        <TextInput appearance="modern"
                            style={{ fontSize: '.75rem' }}
                            type="text" value={event?.slug}
                            error={(event?.slug ?? '').trim() === ''}
                            errorMessage="Campo obbligatorio"
                            placeholder="Slug"
                            onKeyUp={(value) => {
                                const newValue = value?.replace(/[^a-zA-Z0-9\s-]/g, '') // Rimuovi tutti i caratteri tranne lettere e numeri
                                    .replace(/\s+/g, '-') // Sostituisci spazi con trattini
                                    .toLowerCase()
                                    .trim(); // Converti tutto in minuscolo
                                setEvent((prev) => {
                                    prev.slug = newValue
                                    return { ...prev }
                                })
                            }} />
                    </div>

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>DESCRIZIONE</div>
                        <textarea className={infoStyles.description} value={event?.description} onChange={(e) => {
                            setEvent((prev) => {
                                prev.description = e.target.value
                                return { ...prev }
                            })
                        }} />
                    </div>

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>INIZIO</div>
                        <TextInput appearance="modern"
                            type="date"
                            value={event.starts_at ? new Date(event.starts_at + 'Z') : null}
                            defaultValue={event.starts_at ? new Date(event.starts_at + 'Z') : null}
                            placeholder="Inizio"
                            onKeyUp={(d) => {
                                setEvent((prev) => {
                                    prev.starts_at = d.toISOString().split('.')[0]
                                    return { ...prev }
                                })
                            }}
                        />
                    </div>
                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>FINE</div>
                        <TextInput appearance="modern"
                            type="date"
                            value={event.ends_at ? new Date(event.ends_at + 'Z') : null}
                            defaultValue={event.ends_at ? new Date(event.ends_at + 'Z') : null}
                            placeholder="Fine"
                            onKeyUp={(d) => {
                                setEvent((prev) => {
                                    prev.ends_at = d.toISOString().split('.')[0]
                                    return { ...prev }
                                })
                            }}
                        />
                    </div>

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>LOCATION</div>
                        <TextInput appearance="modern"
                            type="text"
                            value={event.location?.name ?? ''}
                            defaultValue={event.location?.name ?? ''}
                            placeholder="Nome Location"
                            onKeyUp={(value) => {
                                setEvent((prev) => {
                                    prev.location = { ...prev.location, name: value }
                                    return { ...prev }
                                })
                            }}
                        />
                        <TextInput appearance="modern"
                            type="text"
                            value={event.location?.address ?? ''}
                            defaultValue={event.location?.address ?? ''}
                            placeholder="Indirizzo Location"
                            onKeyUp={(value) => {
                                setEvent((prev) => {
                                    prev.location = { ...prev.location, address: value }
                                    return { ...prev }
                                })
                            }}
                        />
                        <TextInput appearance="modern"
                            type="number"
                            numberType="float"
                            decimals={10}
                            value={event.location?.latitude ?? ''}
                            defaultValue={event.location?.latitude ?? ''}
                            placeholder="Latitudine"
                            onKeyUp={(value) => {
                                setEvent((prev) => {
                                    prev.location = { ...prev.location, latitude: value }
                                    return { ...prev }
                                })
                            }}
                        />
                        <TextInput appearance="modern"
                            type="number"
                            numberType="float"
                            decimals={10}
                            value={event.location?.longitude ?? ''}
                            defaultValue={event.location?.longitude ?? ''}
                            placeholder="Longitudine"
                            onKeyUp={(value) => {
                                setEvent((prev) => {
                                    prev.location = { ...prev.location, longitude: value }
                                    return { ...prev }
                                })
                            }}
                        />
                    </div>

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>THUMBNAIL</div>
                        <TextInput appearance="modern"
                            style={{ fontSize: '.75rem' }}
                            disabled={event?.thumbnail?.startsWith('blob:')}
                            type="text" value={event?.thumbnail}
                            error={event?.status !== 'draft' && (event?.thumbnail === undefined || event?.thumbnail === null || event?.thumbnail?.trim() === '')}
                            errorMessage="Campo obbligatorio"
                            placeholder="Thumbnail URL"
                            onKeyUp={(value) => {
                                setEvent((prev) => {
                                    prev.thumbnail = value ? value : ''
                                    return { ...prev }
                                })
                            }} />

                        <Uploader
                            crop
                            aspect={16 / 9}
                            shape={'rect'}
                            preview={event.thumbnail}
                            reset={uploaderReset}
                            multiple={false}
                            type={FileType.Image}
                            iconButtons={true}
                            onComplete={(uploads) => {
                                setEvent((prev) => {
                                    prev.thumbnail = uploads[0].url
                                    return { ...prev }
                                })
                            }}
                            onDelete={() => {
                                setEvent((prev) => {
                                    prev.thumbnail = ""
                                    return { ...prev }
                                })
                            }}
                        />
                    </div>

                    <div className={infoStyles.editSection}>
                        <div className={infoStyles.label}>STANDS</div>
                        <button
                            type="button"
                            className={styles.addStandButton}
                            onClick={() => {
                                setEvent((prev) => ({
                                    ...prev,
                                    stands: [
                                        ...(Array.isArray(prev?.stands) ? prev.stands : []),
                                        { name: '', company_name: '', description: '', company_logo: '', company_id: null }
                                    ]
                                }))
                            }}
                        >
                            AGGIUNGI STAND
                        </button>

                        {(Array.isArray(event?.stands) ? event.stands : []).map((stand, index) => (
                            <div key={`stand-${index}`} className={styles.standCard}>
                                <TextInput appearance="modern"
                                    style={{ fontSize: '.75rem' }}
                                    type="text"
                                    value={stand?.name || ''}
                                    placeholder="Nome stand"
                                    onKeyUp={(value) => {
                                        setEvent((prev) => {
                                            const stands = Array.isArray(prev?.stands) ? [...prev.stands] : [];
                                            stands[index] = { ...stands[index], name: value };
                                            return { ...prev, stands };
                                        })
                                    }}
                                />
                                <Select appearance="modern"
                                    selected={eventPartnerCompanies.find((company) => String(company.value) === String(stand?.company_id)) || null}
                                    options={eventPartnerCompanies}
                                    nullable
                                    nullLabel="Nessuna azienda associata"
                                    placeholder="Associa azienda Event Partner"
                                    onSelect={(company) => {
                                        setEvent((prev) => {
                                            const stands = Array.isArray(prev?.stands) ? [...prev.stands] : [];
                                            stands[index] = {
                                                ...stands[index],
                                                company_id: company?.value || null,
                                                company_name: company?.name || stands[index]?.company_name || '',
                                                company_logo: company?.logo || stands[index]?.company_logo || '',
                                            };
                                            return { ...prev, stands };
                                        })
                                    }}
                                />
                                <TextInput appearance="modern"
                                    style={{ fontSize: '.75rem' }}
                                    type="text"
                                    value={stand?.company_name || ''}
                                    placeholder="Nome azienda"
                                    onKeyUp={(value) => {
                                        setEvent((prev) => {
                                            const stands = Array.isArray(prev?.stands) ? [...prev.stands] : [];
                                            stands[index] = { ...stands[index], company_name: value };
                                            return { ...prev, stands };
                                        })
                                    }}
                                />
                                <textarea
                                    className={styles.standDescription}
                                    value={stand?.description || ''}
                                    placeholder="Descrizione stand"
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setEvent((prev) => {
                                            const stands = Array.isArray(prev?.stands) ? [...prev.stands] : [];
                                            stands[index] = { ...stands[index], description: value };
                                            return { ...prev, stands };
                                        })
                                    }}
                                />
                                <TextInput appearance="modern"
                                    style={{ fontSize: '.75rem' }}
                                    type="text"
                                    value={stand?.company_logo || ''}
                                    placeholder="Logo azienda (URL)"
                                    onKeyUp={(value) => {
                                        setEvent((prev) => {
                                            const stands = Array.isArray(prev?.stands) ? [...prev.stands] : [];
                                            stands[index] = { ...stands[index], company_logo: value };
                                            return { ...prev, stands };
                                        })
                                    }}
                                />
                                <Uploader
                                    crop
                                    aspect={1}
                                    shape={'rect'}
                                    preview={stand?.company_logo || ''}
                                    multiple={false}
                                    type={FileType.Image}
                                    iconButtons={true}
                                    onComplete={(uploads) => {
                                        setEvent((prev) => {
                                            const stands = Array.isArray(prev?.stands) ? [...prev.stands] : [];
                                            stands[index] = { ...stands[index], company_logo: uploads?.[0]?.url || '' };
                                            return { ...prev, stands };
                                        })
                                    }}
                                    onDelete={() => {
                                        setEvent((prev) => {
                                            const stands = Array.isArray(prev?.stands) ? [...prev.stands] : [];
                                            stands[index] = { ...stands[index], company_logo: '' };
                                            return { ...prev, stands };
                                        })
                                    }}
                                />
                                <button
                                    type="button"
                                    className={styles.removeStandButton}
                                    onClick={() => {
                                        setEvent((prev) => {
                                            const stands = Array.isArray(prev?.stands) ? [...prev.stands] : [];
                                            stands.splice(index, 1);
                                            return { ...prev, stands };
                                        })
                                    }}
                                >
                                    RIMUOVI STAND
                                </button>
                            </div>
                        ))}
                    </div>

                </div>
            }
        </div>

    return (
        <HeaderFooterLayout hideHeader hideFooter>
            <HelmetProvider>
                <Helmet>
                    <title>{event ? `${event.name} - Editor` : "Evento - Editor"}</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.preview}>
                    {event &&
                        <PageBuilderProvider
                            key={`${event.id || 'create'}-${builderVersion}-${builderKey}`}
                            initialPage={sections}
                            draft={draftSections}
                            initialVersion={builderVersion}
                        >

                            <PageBuilder
                                element={event}
                lastSavedAt={lastSavedAt || event?.updated_at || event?.created_at}
                                customTab={
                                    {
                                        name: "Info",
                                        content: editBar,
                                        saveDisabled: saveDisabled
                                    }
                                }
                                onSave={(page, activeVersion, action) => save(page, activeVersion, action)}
                                isSaving={savingAction !== null}
                                publishButtonStatus={
                                    savingAction === 'publish'
                                        ? ButtonStatus.Loading
                                        : successAction === 'publish'
                                            ? ButtonStatus.Success
                                            : null
                                }
                                saveDraftButtonStatus={
                                    savingAction === 'save_draft'
                                        ? ButtonStatus.Loading
                                        : successAction === 'save_draft'
                                            ? ButtonStatus.Success
                                            : null
                                }
                                onVersionChange={(nextVersion) => {
                                    setBuilderVersion(nextVersion)
                                    setBuilderKey(prev => prev + 1)
                                }} />
                        </PageBuilderProvider>
                    }
                </div>

            </div>
            <AlertDialog
                open={alert.open}
                title={alert.title}
                html={alert.text}
                actions={alert.actions}
                status={alert.status}
                onClose={() => {
                    setAlert((prev) => {
                        prev.open = false
                        return { ...prev }
                    })
                }
                } />
        </HeaderFooterLayout>
    )
}

export default EventEditor
